/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
/**
 * @fileoverview Componente Organism de Template Carousel con arquitectura optimizada
 * @module TemplateTypeCarousel
 * @category Component - Organism
 * @since 1.0.0
 * @version 3.1.0
 */

import React, {
  memo,
  useCallback,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { TrashIcon } from '@heroicons/react/24/outline';
import IconMC from "../../../components/IconMC";
import InputComponent from "../../../components/InputMC";

// Hooks especializados por capa
import useTemplateCarouselForm from "../../../hooks/template-carousel/forms/useTemplateCarouselForm";
import useFileValidation from "../../../hooks/template-carousel/actions/useFileValidation";

/**
 * Componente Organism para manejo de Template Carousel con upload de archivos.
 *
 * Implementa **Atomic Design** como Organism que compone múltiples molecules
 * y atoms para crear funcionalidad completa de carousel de templates.
 */
const TemplateTypeCarousel = memo(({
  carouselData,
  fileConfig = {}
}) => {
  const { t } = useTranslation();
  const formContext = useFormContext();
  const fileInputRefs = useRef({});
  const [activeDropZone, setActiveDropZone] = useState(null);

  // === HOOK COMPOSITION POR CAPAS ===
  // Pure Form Layer - solo manejo de estado del formulario
  const {
    files,
    setFile,
    removeFile,
    updateVariable,
    isFormValid,
    isDirty
  } = useTemplateCarouselForm(carouselData, formContext);

  // Pure Actions Layer - solo lógica de negocio y validación
  const {
    validateFile,
    getFileMetadata,
    formatFileSize
  } = useFileValidation(fileConfig);

  // === ORCHESTRATION LAYER ===
  // Coordinación entre hooks manteniendo separación de responsabilidades

  /**
   * Activa el input de archivo cuando se hace click en el área de carga.
   *
   * Función optimizada que maneja la activación de file inputs
   * con validación de índices y referencias.
   */
  const handleFileAreaClick = useCallback((index) => {
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].click();
    }
  }, []);

  /**
   * Maneja el cambio de archivo seleccionado con validación completa.
   *
   * Orquesta la interacción entre las tres capas:
   * - UI Layer: Estados de loading
   * - Actions Layer: Validación de archivos
   * - Form Layer: Actualización de estado
   */
  const handleFileChange = useCallback(async (event, index) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    try {
      // Actions Layer: Validar archivo
      const validation = await validateFile(file);

      if (validation.isValid) {
        // Form Layer: Actualizar archivo
        const metadata = getFileMetadata(file);
        setFile(index, file, metadata);
      } else {
        // Mostrar alerta al usuario con el error específico
        showtoast(`Error al subir archivo:\n\n${validation.error}`);

        // Limpiar el input
        event.target.value = null;
      }
    } catch (error) {
      console.error('Error procesando archivo:', error);
      event.target.value = null;
    }
  }, [
    validateFile,
    getFileMetadata,
    setFile
  ]);

  /**
   * Maneja la remoción de archivos seleccionados.
   *
   * Coordina la remoción entre Form Layer y UI Layer,
   * incluyendo cleanup de referencias de inputs.
   */
  const handleRemoveFile = useCallback((index) => {
    // Limpiar el input de archivo
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].value = null;
    }

    // Form Layer: Remover archivo
    removeFile(index);
  }, [removeFile]);

  /**
   * Maneja cambios en variables dinámicas del template.
   *
   * Coordina la actualización de variables entre Form Layer
   * y callbacks opcionales al parent component.
   */
  const handleVariableChange = useCallback((fieldName, value) => {
    // Form Layer: Actualizar variable
    updateVariable(fieldName, value);
  }, [updateVariable]);

  // === DRAG & DROP HANDLERS ===

  /**
   * Maneja el evento dragover para permitir el drop.
   *
   * Previene el comportamiento por defecto y establece el efecto de drop.
   */
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  /**
   * Maneja el evento dragenter para activar la drop zone.
   *
   * Activa el feedback visual de la drop zone correspondiente.
   */
  const handleDragEnter = useCallback((event, index) => {
    event.preventDefault();
    const dropZoneId = `dropzone-${index}`;
    setActiveDropZone(dropZoneId);
  }, [setActiveDropZone]);

  /**
   * Maneja el evento dragleave para desactivar la drop zone.
   *
   * Desactiva el feedback visual cuando el drag sale del área.
   */
  const handleDragLeave = useCallback((event) => {
    // Solo limpiar si realmente salimos del área (no de un child)
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setActiveDropZone(null);
    }
  }, [setActiveDropZone]);

  /**
   * Maneja el evento drop para procesar archivos arrastrados.
   *
   * Orquesta el procesamiento de archivos dropped usando las tres capas:
   * - UI Layer: Estados de drop y loading
   * - Actions Layer: Validación de archivos
   * - Form Layer: Actualización de estado
   */
  const handleDrop = useCallback(async (event, index) => {
    event.preventDefault();

    // UI Layer: Limpiar drop zone y iniciar loading
    setActiveDropZone(null);
    // setUploadingState(true);

    try {
      // Extraer archivos del drop
      const droppedFiles = Array.from(event.dataTransfer.files);

      if (droppedFiles.length === 0) {
        return;
      }

      // Tomar solo el primer archivo
      const file = droppedFiles[0];

      // Actions Layer: Validar archivo
      const validation = await validateFile(file);

      if (validation.isValid) {
        // Form Layer: Actualizar archivo
        const metadata = getFileMetadata(file);
        setFile(index, file, metadata);
      } else {
        // Mostrar alerta al usuario con el error específico
        showtoast(`Error en drag & drop:\n\n${validation.error}`);
      }
    } catch (error) {
      console.error('Error procesando archivo dropped:', error);
    }
  }, [
    setActiveDropZone,
    validateFile,
    getFileMetadata,
    setFile
  ]);

  // === RENDER HELPERS ===

  /**
   * Renderiza los inputs para variables dinámicas de la plantilla.
   *
   * Función optimizada que renderiza inputs con integración completa
   * de react-hook-form y manejo de errores.
   */
  const renderInputVariables = useCallback((variablesList, carouselIndex) => {
    if (!variablesList?.length) return null;

    return variablesList.map((field) => (
      <div key={`variable-${field.originalId}-${field.variableNumber}-${carouselIndex}`}>
        <InputComponent
          id={`variable-input-${field.originalId}-${field.variableNumber}-${carouselIndex}`}
          name={field.fieldName}
          label={`${t('NewChat:apiMessage.value')} {{${field.originalId}}}`}
          placeholder={t('NewChat:apiMessage.valuePlaceholder', { value: field.originalId })}
          useFloatingLabel
          required
          onChange={(_, value) => handleVariableChange(field.fieldName, value)}
        />
      </div>
    ));
  }, [t, handleVariableChange]);

  /**
   * Renderiza el archivo seleccionado con información y controles.
   *
   * Component optimizado que muestra información del archivo
   * con capacidad de remoción y metadata formateada.
   */
  const renderSelectedFile = useCallback((fileData, index) => {
    const { file, metadata } = fileData;

    return (
      <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-flex tw-items-center tw-gap-3 tw-mb-4">
        <div className="tw-flex-1 tw-min-w-0">
          <p
            className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate"
            title={file.name}
          >
            {file.name}
          </p>
          <p className="tw-text-xs tw-text-gray-500">
            {metadata?.formattedSize || formatFileSize(file.size)}
          </p>
        </div>
        <button
          type="button"
          className="tw-p-1 hover:tw-bg-gray-100 tw-rounded-full tw-transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveFile(index);
          }}
          aria-label={t('NewChat:apiMessage.removeFile', 'Remover archivo')}
        >
          <TrashIcon className="tw-w-5 tw-h-5 tw-text-gray-400 hover:tw-text-red-500" />
        </button>
      </div>
    );
  }, [formatFileSize, handleRemoveFile, t]);

  /**
   * Renderiza el área de upload de archivos.
   *
   * Component que maneja el área interactiva de upload con
   * estados visuales, feedback de hover y drag & drop.
   */
  const renderUploadArea = useCallback((carousel, index) => {
    const dropZoneId = `dropzone-${index}`;
    const isDragActive = activeDropZone === dropZoneId;

    let uploadAreaLabel;
    if (isDragActive) {
      uploadAreaLabel = t('NewChat:apiMessage.dropFile', 'Suelta el archivo aquí');
    } else if (carousel?.headerType?.toLowerCase() === 'video') {
      uploadAreaLabel = t('ws_templates.create_template.carousel.card.add_video') || 'Agregar video';
    } else {
      uploadAreaLabel = t('ws_templates.create_template.carousel.card.add_image') || 'Agregar imagen';
    }

    return (
      <div
        className={`tw-border tw-border-blue-400 tw-rounded-lg tw-p-4 tw-text-center tw-cursor-pointer tw-mb-4 tw-transition-colors ${isDragActive
          ? 'tw-bg-blue-50 tw-border-blue-500'
          : 'hover:tw-bg-blue-50'
        } ${isDragActive
          ? 'tw-border-dashed tw-border-2 tw-bg-blue-100'
          : ''
        }`}
        onClick={() => handleFileAreaClick(index)}
        onDragOver={handleDragOver}
        onDragEnter={(e) => handleDragEnter(e, index)}
        onDragLeave={(e) => handleDragLeave(e, index)}
        onDrop={(e) => handleDrop(e, index)}
      >
        <input
          type="file"
          ref={(el) => { fileInputRefs.current[index] = el; }}
          className="tw-hidden"
          accept={fileConfig?.accept || ''}
          onChange={(event) => handleFileChange(event, index)}
        />
        <div className="tw-flex tw-justify-center tw-items-center tw-text-blue-500 tw-font-medium">
          <IconMC name="upload-fill" className="tw-mr-2" />
          {uploadAreaLabel}
        </div>
      </div>
    );
  }, [
    activeDropZone,
    fileConfig,
    handleFileAreaClick,
    handleFileChange,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    t
  ]);

  // Clases CSS base
  const containerClasses = "tw-space-y-4";

  // Early return si no hay carousels
  if (!carouselData?.carousels?.length) {
    return (
      <div className="tw-wrapper">
        <div className={containerClasses}>
          <div className="tw-text-center tw-text-gray-500 tw-py-8">
            {t('NewChat:apiMessage.noCarousels', 'No hay carousels disponibles')}
          </div>
        </div>
      </div>
    );
  }

  console.log('Renderizando TemplateTypeCarousel con carousels:', carouselData?.carousels);
  // === MAIN RENDER ===
  return (
    <div className="tw-wrapper">
      <div className={containerClasses}>
        {carouselData?.carousels?.map((carousel, index) => {
          const carouselKey = `carousel-${index}`;
          const fileData = files[index];

          return (
            <div
              key={carouselKey}
              className="tw-border tw-border-gray-200 tw-rounded-lg tw-mb-4 tw-bg-white tw-shadow-sm"
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-bg-blue-50 tw-rounded-t-lg">
                <span className="tw-font-semibold tw-text-gray-900">
                  {carousel?.body?.title}
                </span>
                {isDirty && isFormValid && (
                  <div className="tw-flex tw-items-center tw-text-xs tw-text-green-600">
                    <IconMC name="check-circle" className="tw-w-4 tw-h-4 tw-mr-1" />
                    {t('NewChat:apiMessage.validForm', 'Válido')}
                  </div>
                )}
              </div>

              <div className="tw-p-4">
                {fileData?.file ? (
                  renderSelectedFile(fileData, index)
                ) : (
                  renderUploadArea(carousel, index)
                )}

                {carousel?.body?.variables?.length > 0 && (
                  <div className="tw-space-y-3">
                    <h5 className="tw-text-base tw-font-medium tw-text-gray-900">
                      {t('NewChat:apiMessage.variableValue')}
                    </h5>
                    {renderInputVariables(carousel?.body?.variables, index)}
                  </div>
                )}
                {carousel?.buttons?.length > 0 && (
                  <div className="tw-space-y-3">
                    <h5 className="tw-text-base tw-font-medium tw-text-gray-900">
                      {t('NewChat:apiMessage.button')}
                    </h5>
                    {carousel?.buttons?.map((button, btnIndex) => renderInputVariables(button.variables, btnIndex))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

TemplateTypeCarousel.displayName = "TemplateTypeCarousel";

export default TemplateTypeCarousel;
